/**
 * LeaderboardRow — én rad på Tavla (Plan 02-16; #156, steg 6d, Stell_Tavle).
 *
 * Glassrad med plassen, InitialAvatar (D2: forbokstav i lister), navn og
 * poeng på én linje, og en `ProgressBar` under som viser poengene mot
 * lederens. Kronen står på lederkortet over listen, ikke på raden.
 *
 * Den innloggede får kanten i sin egen personfarge, som Mia i skissen, og
 * `aria-current`, så fargen ikke er eneste kjennetegn for skjermlesere.
 */
import type { ReactNode } from 'react';
import { asColorSlot, InitialAvatar } from '../../components/primitives/InitialAvatar';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { ProgressBar } from '../../components/primitives/ProgressBar';
import { GLASS_FILL } from '../../components/primitives/Card';
import { formatPoints } from '../../i18n/formatters';
import type { LeaderRow } from './useLeaderboardQueries';

/** Hele klassenavn, så Tailwind finner dem ved bygging. */
const VIEWER_BORDER: Record<ColorSlot, string> = {
  'm-terracotta': 'border-m-terracotta/60',
  'm-berry': 'border-m-berry/60',
  'm-ochre': 'border-m-ochre/60',
  'm-sage': 'border-m-sage/60',
  'm-sky': 'border-m-sky/60',
  'm-plum': 'border-m-plum/60',
};

interface LeaderboardRowProps {
  row: LeaderRow;
  /** Lederens poeng; fyller linjen helt. */
  top: number;
  /** Raden til den innloggede. */
  isCurrentViewer: boolean;
}

export function LeaderboardRow({ row, top, isCurrentViewer }: LeaderboardRowProps): ReactNode {
  const slot = asColorSlot(row.slot);
  return (
    <li
      data-testid="leaderboard-row"
      aria-current={isCurrentViewer ? 'true' : undefined}
      className={[
        GLASS_FILL,
        'flex items-center gap-3 rounded-row border px-3.5 py-2.5',
        isCurrentViewer ? VIEWER_BORDER[slot] : 'border-card-line',
      ].join(' ')}
    >
      <span
        data-testid="leaderboard-row-rank"
        className="tnum w-5.5 shrink-0 text-base font-extrabold text-ink-2"
      >
        {row.rank}
      </span>
      <InitialAvatar name={row.short_name} colorSlot={slot} size={40} decorative />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2 text-body font-extrabold text-ink">
          <span className="truncate">{row.short_name}</span>
          <span data-testid="leaderboard-row-points" className="tnum shrink-0">
            <span data-testid="leaderboard-row-points-digit">{formatPoints(row.points)}</span> p
          </span>
        </div>
        <ProgressBar
          value={row.points}
          max={top}
          label={row.short_name}
          className="mt-1.5"
          data-testid="leaderboard-row-bar"
        />
      </div>
    </li>
  );
}
